import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, ListItemText, MenuItem, Radio, RadioGroup, Switch } from '@mui/material';
import { SelectMenu, toast } from '@livable/saas-react-components';
import { api } from 'src/api';
import { useStore } from 'src/stores';
import { currencyFormatter } from 'src/utils/translator';
import { COLORS, MEASURE } from '../ReimbursementReceiptsCustomTab/palette';
import { Panel, KpiCard, EmptyState } from '../ReimbursementReceiptsCustomTab/components';
import useStyles from '../ReimbursementReceiptsCustomTab/styles';
import { CoverageBar, ServiceVarianceBar, TopPropertiesBar } from './charts';
import { fetchUtilityAllocationExport, EMPTY_REPORT } from './data';
import { AUDIENCES, planFrom, skipReasonLabel } from './monthlyReport';
import { nextPropertySelection, ALL_PROPERTIES } from './propertyFilter';
const NO_OPTIONS = {
  cancelToken: undefined
};
const usd = v => currencyFormatter(Number(v) || 0);
const pct = v => "".concat((Number(v) || 0).toFixed(1), "%");
const IN_BALANCE = 0.005; // |variance| under half a cent = balanced
const varianceColor = v => Math.abs(v) < IN_BALANCE ? MEASURE.paid : v > 0 ? MEASURE.unpaid : COLORS.gold;
const OUTLINED_BUTTON = {
  textTransform: 'none',
  borderColor: COLORS.border,
  color: COLORS.teal,
  flexShrink: 0,
  whiteSpace: 'nowrap'
};

// A utility can have an allocation table before its bill is uploaded. That is a missing bill, not $0.00.
const NO_BILL = '—';
const money = v => v === null || v === undefined ? NO_BILL : Number(v).toFixed(2);
const renderVariance = (classes, v) => v === null || v === undefined ? /*#__PURE__*/React.createElement("td", {
  className: classes.num,
  style: {
    color: COLORS.textFaint
  }
}, NO_BILL) : /*#__PURE__*/React.createElement("td", {
  className: classes.num,
  style: {
    color: varianceColor(v),
    fontWeight: 600
  }
}, Number(v).toFixed(2));

// Property Summary — shaded row = no invoices this period (upload gap), red unrecovered > 0,
// green = full recovery (Metabase card #344).
function renderPropertySummary(classes, rows) {
  if (!rows.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No property summary for this period."
  });
  const rowBg = r => r.invoices_in_period === 0 ? 'rgba(235,153,110,0.10)' : undefined;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property address"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Utilities configured"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Billed this period"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Invoices"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total invoiced ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Unrecovered ($)"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(r.property_address, "-").concat(i),
    style: {
      background: rowBg(r)
    }
  }, /*#__PURE__*/React.createElement("td", null, r.property_address), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.utilities_configured), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.utilities_billed_this_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.invoices_in_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, Number(r.total_invoiced).toFixed(2)), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, Number(r.total_allocated).toFixed(2)), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: varianceColor(r.owner_billable),
      fontWeight: 600
    }
  }, Number(r.owner_billable).toFixed(2)))))));
}
function renderInvoiceSummary(classes, rows) {
  if (!rows.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No invoices for this period."
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property address"), /*#__PURE__*/React.createElement("th", null, "Service"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total bill ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Variance ($)"), /*#__PURE__*/React.createElement("th", null, "Bill period"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Residents"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(r.property_address, "-").concat(r.service, "-").concat(i)
  }, /*#__PURE__*/React.createElement("td", null, r.property_address), /*#__PURE__*/React.createElement("td", null, r.service || /*#__PURE__*/React.createElement("span", {
    style: {
      color: COLORS.textFaint
    }
  }, "Unmapped utility")), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, money(r.total_bill_amount)), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.total_allocated.toFixed(2)), renderVariance(classes, r.variance), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, r.bill_period), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, r.active_residents))))));
}

// Nested export — each invoice is a header row (shaded) followed by its tenant charge rows
// (indented, muted). Mirrors Metabase card #333 (PRD §6.2).
function renderNested(classes, groups) {
  if (!groups.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No invoices for this period."
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.tableWrap, " ").concat(classes.tableScroll)
  }, /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Property / Tenant"), /*#__PURE__*/React.createElement("th", null, "Service / Unit"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Total bill ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Allocated ($)"), /*#__PURE__*/React.createElement("th", {
    className: classes.num
  }, "Variance / Charge ($)"), /*#__PURE__*/React.createElement("th", null, "Bill period"))), /*#__PURE__*/React.createElement("tbody", null, groups.map((g, gi) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: "".concat(g.property_address, "-").concat(g.service, "-").concat(gi)
  }, /*#__PURE__*/React.createElement("tr", {
    style: {
      background: 'rgba(0,146,153,0.06)'
    }
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      fontWeight: 700
    }
  }, g.property_address), /*#__PURE__*/React.createElement("td", {
    style: {
      fontWeight: 600
    }
  }, g.service), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      fontWeight: 600
    }
  }, money(g.total_bill_amount)), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      fontWeight: 600
    }
  }, Number(g.total_allocated).toFixed(2)), renderVariance(classes, g.variance), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, g.bill_period)), (g.tenants || []).map((t, ti) => /*#__PURE__*/React.createElement("tr", {
    key: "".concat(gi, "-t-").concat(ti)
  }, /*#__PURE__*/React.createElement("td", {
    style: {
      paddingLeft: 34,
      color: COLORS.textMuted
    }
  }, t.name), /*#__PURE__*/React.createElement("td", {
    style: {
      color: COLORS.textMuted
    }
  }, t.unit), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      color: COLORS.text
    }
  }, Number(t.charge).toFixed(2)), /*#__PURE__*/React.createElement("td", null))))))));
}
function renderSkipSummary(classes, rows) {
  if (!rows) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "Skipped bills are not available for this period."
  });
  if (!rows.length) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "No bills were skipped."
  });
  return /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("tbody", null, rows.map(s => /*#__PURE__*/React.createElement("tr", {
    key: s.reason
  }, /*#__PURE__*/React.createElement("td", null, skipReasonLabel(s.reason)), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, s.count)))));
}

// Admin fees are charged to residents and credited to the property manager, so they offset what the
// manager pays Livable. The subscription is company-wide, so it is only compared with unfiltered fees.
function renderFees(classes, adminFees, plan, isFiltered) {
  if (adminFees === null) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "Admin fees are not available for this period."
  });
  const net = plan ? adminFees - plan.monthlyTotal : null;
  return /*#__PURE__*/React.createElement("table", {
    className: classes.table
  }, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Admin fees billed to residents"), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, usd(adminFees))), isFiltered && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: 2,
    style: {
      color: COLORS.textMuted
    }
  }, "Clear the property filter to compare with your Livable subscription.")), plan && !isFiltered && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Livable subscription (monthly)"), /*#__PURE__*/React.createElement("td", {
    className: classes.num
  }, usd(plan.monthlyTotal))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    style: {
      fontWeight: 700
    }
  }, "Net"), /*#__PURE__*/React.createElement("td", {
    className: classes.num,
    style: {
      fontWeight: 700,
      color: net >= 0 ? COLORS.green : MEASURE.unpaid
    }
  }, net >= 0 ? "+".concat(usd(net)) : usd(net))))));
}

// plan: undefined while loading, null when there is no active subscription or it could not be read.
function renderPlan(plan, handleUpgradeClick) {
  if (plan === undefined) return /*#__PURE__*/React.createElement(CircularProgress, {
    size: 20,
    style: {
      color: COLORS.teal
    }
  });
  if (!plan) return /*#__PURE__*/React.createElement(EmptyState, {
    message: "Plan details are unavailable."
  });
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      alignItems: 'flex-start'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 700,
      color: COLORS.text
    }
  }, plan.isBillingAutomation ? 'Billing Automation' : 'Self-Service'), !plan.isBillingAutomation && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    style: {
      color: COLORS.textMuted
    }
  }, "With Billing Automation, Livable collects your utility bills and allocates them for you."), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleUpgradeClick,
    style: {
      textTransform: 'none'
    }
  }, "Upgrade to Billing Automation")));
}

// createAsync already toasts a thrown error and resolves undefined, so only a 2xx that failed is reported here.
const failed = (resp, message) => {
  if (resp && !resp.isSuccessful) toast('error', {
    body: [message]
  });
  return !(resp !== null && resp !== void 0 && resp.isSuccessful);
};
const EmailReportDialog = _ref => {
  let open = _ref.open,
    onClose = _ref.onClose,
    allocationCycleId = _ref.allocationCycleId,
    cycleLabel = _ref.cycleLabel,
    store = _ref.store;
  const _useState = useState(AUDIENCES[0].value),
    _useState2 = _slicedToArray(_useState, 2),
    audience = _useState2[0],
    setAudience = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSending = _useState4[0],
    setIsSending = _useState4[1];

  // Every open starts from "me", so a company-wide send is always a fresh choice for the period on screen.
  useEffect(() => {
    if (open) setAudience(AUDIENCES[0].value);
  }, [open]);
  const handleSend = async () => {
    setIsSending(true);
    try {
      const resp = await store.report.sendMonthlyReportAsync.run({
        allocationCycleId,
        audience
      });
      if (failed(resp, 'The report could not be sent.')) return;
      toast('success', {
        body: ["".concat(cycleLabel, " report sent")]
      });
      onClose();
    } finally {
      setIsSending(false);
    }
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm"
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Email the ", cycleLabel, " report"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, "Sends the recovery summary for this billing period across every property you can see, whatever the filter. Recipients can open the full report in Livable."), /*#__PURE__*/React.createElement(RadioGroup, {
    value: audience,
    onChange: e => setAudience(e.target.value)
  }, AUDIENCES.map(a => /*#__PURE__*/React.createElement(FormControlLabel, {
    key: a.value,
    value: a.value,
    control: /*#__PURE__*/React.createElement(Radio, {
      id: "uae-audience-".concat(a.value)
    }),
    label: a.label
  })))), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: isSending,
    onClick: handleSend
  }, "Send report")));
};
const MonthlyEmailDialog = _ref2 => {
  let open = _ref2.open,
    onClose = _ref2.onClose,
    settings = _ref2.settings,
    canManage = _ref2.canManage,
    onSaved = _ref2.onSaved,
    store = _ref2.store;
  const _useState5 = useState(settings),
    _useState6 = _slicedToArray(_useState5, 2),
    draft = _useState6[0],
    setDraft = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isSaving = _useState8[0],
    setIsSaving = _useState8[1];
  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const body = {
        emailEnabled: draft.emailEnabled
      };
      const resp = await store.report.saveMonthlyReportSettingsAsync.run(body);
      if (failed(resp, 'Monthly email settings could not be saved.')) return;
      onSaved(_objectSpread(_objectSpread({}, settings), body));
      toast('success', {
        body: ['Monthly email settings saved']
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };
  if (!draft) return null;
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm"
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Monthly recovery report email"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, "Account Holders with access to every property receive last month's recovery report on the 16th.", canManage ? '' : ' Only an Account Holder can change this.'), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      id: "uae-monthly-email-enabled",
      checked: draft.emailEnabled,
      disabled: !canManage,
      onChange: e => setDraft(_objectSpread(_objectSpread({}, draft), {}, {
        emailEnabled: e.target.checked
      }))
    }),
    label: "Send the monthly recovery report"
  }), (settings === null || settings === void 0 ? void 0 : settings.lastSentOn) && /*#__PURE__*/React.createElement(DialogContentText, {
    style: {
      marginTop: 12
    }
  }, "Last sent ", new Date(settings.lastSentOn).toLocaleDateString('en-US'), ".")), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose
  }, canManage ? 'Cancel' : 'Close'), canManage && /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: isSaving,
    onClick: handleSave
  }, "Save")));
};
const UpgradeDialog = _ref3 => {
  let open = _ref3.open,
    onClose = _ref3.onClose,
    plan = _ref3.plan,
    store = _ref3.store;
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isRequesting = _useState0[0],
    setIsRequesting = _useState0[1];
  // Only a confirmed Self-Service plan can request; a plan still loading or unknown cannot.
  const isAlreadyUpgraded = !plan || plan.isBillingAutomation;
  const handleRequest = async () => {
    setIsRequesting(true);
    try {
      const resp = await store.report.requestBillingAutomationUpgradeAsync.run();
      if (failed(resp, 'The upgrade request could not be sent.')) return;
      toast('success', {
        body: ['Request sent. Your account manager will follow up.']
      });
      onClose();
    } finally {
      setIsRequesting(false);
    }
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm"
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Upgrade to Billing Automation"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, isAlreadyUpgraded ? plan ? 'Your company is already on Billing Automation.' : 'Your plan could not be confirmed, so an upgrade cannot be requested here. Contact your account manager.' : 'Livable collects your utility bills and allocates them for you. Send a request and your account manager will follow up. Nothing on your plan changes until you agree to it.')), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose
  }, isAlreadyUpgraded ? 'Close' : 'Cancel'), !isAlreadyUpgraded && /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: isRequesting,
    onClick: handleRequest
  }, "Request upgrade")));
};

// "Utility Allocation Export" — native (non-embedded) rebuild of Metabase dashboard #101.
// KPIs + coverage + breakdowns + property summary + flat invoice list + nested invoice/tenant
// detail. Data is live from the app's allocations API (same DB Metabase reads).
export const UtilityAllocationExportTab = _ref4 => {
  let _ref4$upgradeLinked = _ref4.upgradeLinked,
    upgradeLinked = _ref4$upgradeLinked === void 0 ? false : _ref4$upgradeLinked,
    _ref4$onUpgradeLinkHa = _ref4.onUpgradeLinkHandled,
    onUpgradeLinkHandled = _ref4$onUpgradeLinkHa === void 0 ? () => {} : _ref4$onUpgradeLinkHa;
  const classes = useStyles();
  const store = useStore();
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    report = _useState10[0],
    setReport = _useState10[1]; // null = loading
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    selectedCycleId = _useState12[0],
    setSelectedCycleId = _useState12[1];
  // [] = every property. Held separately from the option list so filtering never shrinks the options.
  const _useState13 = useState([]),
    _useState14 = _slicedToArray(_useState13, 2),
    selectedPropertyIds = _useState14[0],
    setSelectedPropertyIds = _useState14[1];
  const _useState15 = useState([]),
    _useState16 = _slicedToArray(_useState15, 2),
    propertyOptions = _useState16[0],
    setPropertyOptions = _useState16[1];
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isExporting = _useState18[0],
    setIsExporting = _useState18[1];
  const _useState19 = useState(undefined),
    _useState20 = _slicedToArray(_useState19, 2),
    plan = _useState20[0],
    setPlan = _useState20[1]; // undefined = loading, null = none or unreadable
  const _useState21 = useState(null),
    _useState22 = _slicedToArray(_useState21, 2),
    settings = _useState22[0],
    setSettings = _useState22[1];
  const _useState23 = useState(null),
    _useState24 = _slicedToArray(_useState23, 2),
    openDialog = _useState24[0],
    setOpenDialog = _useState24[1]; // 'email' | 'settings' | 'upgrade'
  // Token-backed: the profile's userRole is still NonSpecified on a cold load, and this tab does not re-render on it.
  const canManageEmail = store.account.userProfile.isUserPmHolderOrHigher;
  useEffect(() => {
    let alive = true;
    setReport(null);
    fetchUtilityAllocationExport(store, selectedCycleId, selectedPropertyIds).then(r => {
      var _r$propertySummary;
      if (!alive) return;
      setReport(r);
      // Adopt whichever cycle the data layer picked, so the select reflects what is on screen.
      if (!selectedCycleId && r.cycleId) setSelectedCycleId(r.cycleId);
      // The unfiltered response lists every property the caller may see, so it is the option
      // list. Captured once - a filtered response only contains the selection.
      if (!selectedPropertyIds.length && (_r$propertySummary = r.propertySummary) !== null && _r$propertySummary !== void 0 && _r$propertySummary.length) {
        setPropertyOptions(r.propertySummary.map(p => ({
          id: p.property_id,
          address: p.property_address
        })).filter(p => p.id));
      }
    }).catch(() => alive && setReport(EMPTY_REPORT));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycleId, selectedPropertyIds]);
  useEffect(() => {
    api.Stripe.getSubscriptionCompany(undefined, NO_OPTIONS).then(resp => {
      var _resp$response;
      return setPlan(planFrom(resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.subscriptions));
    }).catch(() => setPlan(null));
    api.Allocations.getMonthlyReportSettings(NO_OPTIONS).then(resp => {
      var _resp$response2;
      return setSettings((_resp$response2 = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response2 !== void 0 ? _resp$response2 : null);
    }).catch(() => setSettings(null));
  }, []);

  // Opened from the monthly email's upgrade link. Wait for the plan, so the dialog only opens for a company
  // confirmed to be on Self-Service.
  useEffect(() => {
    if (!upgradeLinked || plan === undefined) return;
    if (plan && !plan.isBillingAutomation) setOpenDialog('upgrade');
    // Once per link: returning to this tab later must not reopen it.
    onUpgradeLinkHandled();
  }, [plan, upgradeLinked, onUpgradeLinkHandled]);
  const handlePropertyChange = values => setSelectedPropertyIds(nextPropertySelection(values));
  const handleDialogClose = () => setOpenDialog(null);
  const handleExport = async () => {
    setIsExporting(true);
    try {
      var _resp$headers$fileNa, _resp$headers;
      // Through the store rather than the api layer: createAsync toasts the failure and tracks it as
      // an amplitude api-error. A silent download failure is untraceable. (The report itself still
      // loads through the api layer - those store actions overwrite shared allocation state.)
      const resp = await store.report.downloadUtilityAllocationExportAsync.run({
        allocationCycleId: selectedCycleId,
        monthsBack: 12,
        propertyIds: selectedPropertyIds
      });
      if (!(resp !== null && resp !== void 0 && resp.data)) return;
      const fileName = (_resp$headers$fileNa = (_resp$headers = resp.headers) === null || _resp$headers === void 0 ? void 0 : _resp$headers['file-name']) !== null && _resp$headers$fileNa !== void 0 ? _resp$headers$fileNa : 'utility-allocation-export.csv';
      const url = window.URL.createObjectURL(new Blob([resp.data], {
        type: 'application/octet-stream'
      }));
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };
  if (report === null) {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.root
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        padding: 64,
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(CircularProgress, {
      size: 26,
      style: {
        color: COLORS.teal
      }
    })));
  }
  const kpis = report.kpis,
    coverage = report.coverage,
    byService = report.byService,
    topProperties = report.topProperties,
    propertySummary = report.propertySummary,
    invoices = report.invoices,
    cycles = report.cycles,
    hasTenantDetail = report.hasTenantDetail;
  const hasData = invoices.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h2", {
    className: classes.title
  }, "Utility Allocation Export"), /*#__PURE__*/React.createElement("p", {
    className: classes.subtitle
  }, "Monthly per-invoice variance and tenant detail: utility bills vs amounts recovered from residents.", report.cycleLabel ? " Billing period: ".concat(report.cycleLabel) : '', report.cycleLabel && !report.cycleIsClosed ? report.cycleResolvedPct === null ? ' (cycle still open, bills may be missing).' : " (".concat(pct(report.cycleResolvedPct), " complete, ").concat(report.cycleOpenTables, " allocations still open).") : '.')), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'flex-end'
    }
  }, propertyOptions.length > 1 && /*#__PURE__*/React.createElement(SelectMenu, {
    id: "uae-property",
    label: "Property",
    multiple: true,
    value: selectedPropertyIds,
    renderValue: () => {
      var _propertyOptions$find, _propertyOptions$find2;
      return selectedPropertyIds.length === 0 ? 'All properties' : selectedPropertyIds.length === 1 ? (_propertyOptions$find = (_propertyOptions$find2 = propertyOptions.find(p => p.id === selectedPropertyIds[0])) === null || _propertyOptions$find2 === void 0 ? void 0 : _propertyOptions$find2.address) !== null && _propertyOptions$find !== void 0 ? _propertyOptions$find : '1 property' : "".concat(selectedPropertyIds.length, " properties");
    },
    onChange: e => handlePropertyChange(e.target.value),
    style: {
      minWidth: 220
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ALL_PROPERTIES,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.length === 0
  }), /*#__PURE__*/React.createElement(ListItemText, null, "All properties")), propertyOptions.map(p => /*#__PURE__*/React.createElement(MenuItem, {
    key: p.id,
    value: p.id,
    variant: "multiple"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: selectedPropertyIds.includes(p.id)
  }), /*#__PURE__*/React.createElement(ListItemText, null, p.address)))), cycles.length > 0 && /*#__PURE__*/React.createElement(SelectMenu, {
    id: "uae-cycle",
    label: "Billing period",
    value: selectedCycleId !== null && selectedCycleId !== void 0 ? selectedCycleId : '',
    onChange: e => setSelectedCycleId(e.target.value),
    style: {
      minWidth: 200
    }
  }, cycles.map(c => /*#__PURE__*/React.createElement(MenuItem, {
    key: c.id,
    value: c.id
  }, c.label))), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    disabled: !hasData || isExporting,
    onClick: handleExport,
    style: OUTLINED_BUTTON
  }, "Export to CSV"), canManageEmail && (settings === null || settings === void 0 ? void 0 : settings.sendingEnabled) && /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    disabled: !hasData,
    onClick: () => setOpenDialog('email'),
    style: OUTLINED_BUTTON
  }, "Email report"), (settings === null || settings === void 0 ? void 0 : settings.sendingEnabled) && /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    onClick: () => setOpenDialog('settings'),
    style: OUTLINED_BUTTON
  }, "Monthly email"))), !hasData ? /*#__PURE__*/React.createElement(EmptyState, {
    message: "No allocation data for this billing period."
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.kpiRow
  }, /*#__PURE__*/React.createElement(KpiCard, {
    label: "Utility Total $",
    value: usd(kpis.totalInvoice)
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Allocated to Residents $",
    value: usd(kpis.totalAllocated),
    tone: COLORS.green
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Unrecovered $",
    value: usd(kpis.ownerBillable),
    tone: kpis.ownerBillable > 0 ? MEASURE.unpaid : COLORS.green,
    footnote: "Bills minus resident allocations, not unpaid balances"
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "Recovery %",
    value: pct(kpis.recoveryPct * 100),
    tone: kpis.recoveryPct >= 0.999 ? COLORS.green : COLORS.gold
  }), /*#__PURE__*/React.createElement(KpiCard, {
    label: "# Invoices",
    value: kpis.invoiceCount
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.grid
  }, /*#__PURE__*/React.createElement(Panel, {
    title: "Bills skipped this month",
    subtitle: "A skipped bill is not allocated to residents, which lowers recovery",
    span: 4
  }, renderSkipSummary(classes, report.skipSummary)), /*#__PURE__*/React.createElement(Panel, {
    title: "Admin fees and your Livable subscription",
    subtitle: "Admin fees billed to residents are credited to you",
    span: 4
  }, renderFees(classes, report.adminFeesBilled, plan, selectedPropertyIds.length > 0)), /*#__PURE__*/React.createElement(Panel, {
    title: "Your plan",
    subtitle: "From your Livable subscription",
    span: 4
  }, renderPlan(plan, () => setOpenDialog('upgrade'))), /*#__PURE__*/React.createElement(Panel, {
    title: "Coverage Health \u2014 Last 12 Months",
    subtitle: "Invoice volume per cycle",
    span: 12,
    height: 240
  }, coverage.length ? /*#__PURE__*/React.createElement(CoverageBar, {
    data: coverage
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "No coverage history."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Unrecovered by Service",
    subtitle: "Utility cost not allocated to residents, by utility",
    span: 6,
    accent: MEASURE.unpaid,
    height: 280
  }, byService.length ? /*#__PURE__*/React.createElement(ServiceVarianceBar, {
    data: byService
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "Nothing unrecovered."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Top Properties by Unrecovered Amount",
    subtitle: "Highest unrecovered cost",
    span: 6,
    accent: MEASURE.unpaid,
    height: 280
  }, topProperties.length ? /*#__PURE__*/React.createElement(TopPropertiesBar, {
    data: topProperties
  }) : /*#__PURE__*/React.createElement(EmptyState, {
    message: "Nothing unrecovered."
  })), /*#__PURE__*/React.createElement(Panel, {
    title: "Property Summary",
    subtitle: "Per active property \u2014 utilities configured vs billed, invoiced vs allocated",
    span: 12
  }, renderPropertySummary(classes, propertySummary)), /*#__PURE__*/React.createElement(Panel, {
    title: "Invoice Summary",
    subtitle: "Flat per-invoice view \u2014 billed vs allocated vs variance",
    span: 12
  }, renderInvoiceSummary(classes, invoices)), hasTenantDetail && /*#__PURE__*/React.createElement(Panel, {
    title: "Utility Allocation Export",
    subtitle: "Invoice header rows with tenant charge detail beneath",
    span: 12
  }, renderNested(classes, invoices)))), /*#__PURE__*/React.createElement(EmailReportDialog, {
    open: openDialog === 'email',
    onClose: handleDialogClose,
    allocationCycleId: selectedCycleId,
    cycleLabel: report.cycleLabel,
    store: store
  }), /*#__PURE__*/React.createElement(MonthlyEmailDialog, {
    open: openDialog === 'settings',
    onClose: handleDialogClose,
    settings: settings,
    canManage: canManageEmail,
    onSaved: setSettings,
    store: store
  }), /*#__PURE__*/React.createElement(UpgradeDialog, {
    open: openDialog === 'upgrade',
    onClose: handleDialogClose,
    plan: plan,
    store: store
  }));
};